import { useState, useEffect, useCallback } from "react";

const FOREST = "#163A2A";
const MINT = "#8FC69B";
const CREAM = "#FAF7F2";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "flavrd_admin_token";

export default function AdminDashboard() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [signups, setSignups] = useState([]);
  const [fetching, setFetching] = useState(false);

  const fetchSignups = useCallback(async (tok) => {
    setFetching(true);
    try {
      const res = await fetch(`${API}/admin/signups`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      if (res.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
        setError("Your session expired. Please sign in again.");
        return;
      }
      const data = await res.json();
      setSignups(data.signups || []);
    } catch (e) {
      setError("Could not load signups. Please try again.");
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchSignups(token);
  }, [token, fetchSignups]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        setError("Incorrect password. Please try again.");
        return;
      }
      const data = await res.json();
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
    } catch (e) {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setSignups([]);
  };

  const downloadCsv = () => {
    const headers = ["Name", "Mobile", "Email", "Signed Up"];
    const rows = signups.map((s) => [
      s.name || "",
      s.mobile || "",
      s.email || "",
      s.created_at ? new Date(s.created_at).toLocaleString() : "",
    ]);
    const escape = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map((r) => r.map(escape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `flavrd-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!token) {
    return (
      <div
        data-testid="admin-login-screen"
        className="min-h-screen flex items-center justify-center px-6"
        style={{ backgroundColor: FOREST }}
      >
        <div className="w-full max-w-sm">
          <div
            className="text-5xl mb-2 select-none"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 800, color: CREAM }}
          >
            Flavrd<span style={{ color: MINT }}>.</span>
          </div>
          <p className="text-[11px] font-bold uppercase tracking-[0.28em] mb-10" style={{ color: MINT }}>
            Waitlist Dashboard
          </p>
          <form onSubmit={handleLogin} data-testid="admin-login-form" className="flex flex-col gap-6">
            <div>
              <label htmlFor="admin-password" className="block text-[11px] font-bold uppercase tracking-[0.2em] mb-3" style={{ color: "rgba(250,247,242,0.45)" }}>
                Password
              </label>
              <input
                id="admin-password"
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); if (error) setError(""); }}
                data-testid="admin-password-input"
                autoComplete="off"
                placeholder="Enter admin password"
                className="w-full bg-transparent text-base pb-3 outline-none"
                style={{
                  color: CREAM,
                  borderBottom: "1px solid rgba(250,247,242,0.2)",
                  fontFamily: "'Inter', sans-serif",
                  caretColor: MINT,
                }}
              />
            </div>
            {error && (
              <p className="text-xs" data-testid="admin-login-error" style={{ color: "rgba(243,180,160,0.9)" }}>
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-button"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-sm tracking-wide transition-all duration-300 hover:opacity-90 active:scale-95 disabled:opacity-60"
              style={{ backgroundColor: MINT, color: FOREST }}
            >
              {loading ? "Signing in…" : "Sign In →"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen" style={{ backgroundColor: CREAM }}>
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-12">
        <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="text-3xl select-none" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 800, color: FOREST }}>
              Flavrd<span style={{ color: MINT }}>.</span>
            </div>
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] mt-1" style={{ color: "rgba(22,58,42,0.55)" }}>
              Waitlist Dashboard
            </p>
          </div>
          <button
            onClick={handleLogout}
            data-testid="admin-logout-button"
            className="text-xs font-semibold uppercase tracking-widest px-5 py-2.5 rounded-full transition-all hover:opacity-80"
            style={{ border: `1px solid ${FOREST}`, color: FOREST }}
          >
            Log Out
          </button>
        </div>

        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <p className="text-base" style={{ color: FOREST }}>
            <span data-testid="admin-signup-total" className="font-bold text-2xl" style={{ fontFamily: "'Playfair Display', serif" }}>
              {signups.length}
            </span>{" "}
            real signup{signups.length === 1 ? "" : "s"}
          </p>
          <button
            onClick={downloadCsv}
            disabled={signups.length === 0}
            data-testid="admin-download-csv-button"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm tracking-wide transition-all duration-300 hover:opacity-90 active:scale-95 disabled:opacity-40"
            style={{ backgroundColor: FOREST, color: CREAM }}
          >
            Download CSV ↓
          </button>
        </div>

        {fetching ? (
          <p className="py-12 text-sm" data-testid="admin-loading" style={{ color: "rgba(22,58,42,0.6)" }}>
            Loading signups…
          </p>
        ) : signups.length === 0 ? (
          <p className="py-12 text-sm" data-testid="admin-empty" style={{ color: "rgba(22,58,42,0.6)" }}>
            No signups yet. Share your landing page to start collecting them.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-2xl" style={{ border: "1px solid rgba(22,58,42,0.12)" }}>
            <table className="w-full text-left" data-testid="admin-signups-table">
              <thead>
                <tr style={{ backgroundColor: "rgba(22,58,42,0.05)" }}>
                  {["Name", "Mobile", "Email", "Signed Up"].map((h) => (
                    <th key={h} className="px-5 py-4 text-[11px] font-bold uppercase tracking-[0.14em]" style={{ color: "rgba(22,58,42,0.6)" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {signups.map((s, i) => (
                  <tr key={s.id || i} data-testid={`admin-signup-row-${i}`} style={{ borderTop: "1px solid rgba(22,58,42,0.08)" }}>
                    <td className="px-5 py-4 text-sm font-medium" style={{ color: FOREST }}>{s.name}</td>
                    <td className="px-5 py-4 text-sm" style={{ color: "rgba(22,58,42,0.75)" }}>{s.mobile || "—"}</td>
                    <td className="px-5 py-4 text-sm" style={{ color: "rgba(22,58,42,0.75)" }}>{s.email || "—"}</td>
                    <td className="px-5 py-4 text-sm" style={{ color: "rgba(22,58,42,0.6)" }}>
                      {s.created_at ? new Date(s.created_at).toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
